const API_URL = import.meta.env.VITE_API_URL || '/api';

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<{ data: T }> {
    const { params, ...fetchOptions } = options;

    let url = `${this.baseUrl}${path}`;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers: {
        ...this.getHeaders(),
        ...fetchOptions.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
      // Handle Zod validation errors which have an 'error' object with 'issues' array
      let errorMessage: string;
      if (typeof errorData.error === 'string') {
        errorMessage = errorData.error;
      } else if (errorData.error?.issues) {
        // Zod validation error - extract first issue message
        errorMessage = errorData.error.issues.map((i: { path: string[]; message: string }) =>
          `${i.path.join('.')}: ${i.message}`
        ).join(', ');
      } else if (errorData.error?.message) {
        errorMessage = errorData.error.message;
      } else {
        errorMessage = `HTTP ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return { data };
  }

  async get<T>(path: string, params?: Record<string, string>): Promise<{ data: T }> {
    return this.request<T>(path, { method: 'GET', params });
  }

  async post<T>(path: string, body?: unknown): Promise<{ data: T }> {
    return this.request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(path: string, body?: unknown): Promise<{ data: T }> {
    return this.request<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(path: string): Promise<{ data: T }> {
    return this.request<T>(path, { method: 'DELETE' });
  }

  async upload<T>(path: string, formData: FormData): Promise<{ data: T }> {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
      // Handle Zod validation errors which have an 'error' object with 'issues' array
      let errorMessage: string;
      if (typeof errorData.error === 'string') {
        errorMessage = errorData.error;
      } else if (errorData.error?.issues) {
        errorMessage = errorData.error.issues.map((i: { path: string[]; message: string }) =>
          `${i.path.join('.')}: ${i.message}`
        ).join(', ');
      } else if (errorData.error?.message) {
        errorMessage = errorData.error.message;
      } else {
        errorMessage = `HTTP ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return { data };
  }
}

export const api = new ApiClient(API_URL);
